import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  CreditCard, 
  Search, 
  Filter, 
  Download, 
  RefreshCw, 
  Eye, 
  Edit, 
  Trash2,
  Plus,
  Calendar,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  FileText,
  User,
  Package,
  DollarSign,
  X,
  ShoppingCart,
  Users,
  IndianRupee,
  Building,
  Phone,
  Mail,
  MapPin,
  CreditCard as PaymentIcon
} from 'lucide-react';
import { paymentTransactionsAPI, orderAPI, userAPI, paymentMethodsAPI } from '../../services/api';
import { mapPaymentMethodToDisplay, mapPaymentStatusToDisplay, mapOrderStatusToDisplay, getStatusBadge, getStatusColor } from '../../utils/paymentUtils';

const EMIPaymentsManagement = () => {


  const [emiPayments, setEmiPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    payment_status: '',
    installment_status: '',
    user_id: '',
    order_id: '',
    search: ''
  });
  
  // Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Dropdown data states
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchOrder, setSearchOrder] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);

  // Installments management
  const [installments, setInstallments] = useState([]);
  const [showInstallmentDetails, setShowInstallmentDetails] = useState(false);

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  // Payment methods mapping from database names to display info
  const paymentMethodsMapping = {
    'cashfree': { label: 'Cashfree Payment', icon: CreditCard },
    'razorpay': { label: 'Razorpay Payment', icon: CreditCard },
    'phonepe': { label: 'PhonePe Payment', icon: Phone },
    'offline': { label: 'Offline Payment', icon: Building },
    'cod': { label: 'Cash on Delivery', icon: IndianRupee }
  };
  
  // Form states
  const [formData, setFormData] = useState({
    order_id: '',
    user_id: '',
    transaction_type: 'emi_payment',
    amount: '',
    payment_method: '',
    payment_status: 'pending',
    installment_status: 'coming_soon',
    transaction_id: '',
    payment_date: '',
    next_payment_date: '',
    next_payment_amount: '',
    total_installments: '',
    current_installment: '',
    remaining_amount: '',
    notes: ''
  });

  // View payment details
  const viewPayment = async (paymentId) => {
    try {
      setViewError(null);
      setViewLoading(true);
      setSelectedPayment({ id: paymentId });
      setShowViewModal(true);
      
      const response = await paymentTransactionsAPI.getById(paymentId);
      
      if (response.data && response.data.success) {
        const paymentData = response.data.data;
        
        // Try to fetch the actual order details to get the correct order number
        if (paymentData.order_id) {
          try {
            const orderResponse = await orderAPI.getById(paymentData.order_id);
            if (orderResponse.data && orderResponse.data.data) {
              const orderData = orderResponse.data.data;
              // Update the payment data with the correct order information
              paymentData.order_number = orderData.order_number;
              paymentData.order_status = orderData.order_status;
            }
          } catch (orderError) {
            console.warn('Could not fetch order details for view:', orderError.message);
            // Continue with the payment data as is
          }
        }
        
        setSelectedPayment(paymentData);
        setShowViewModal(true);
        setViewLoading(false);
      } else {
        console.error('View EMI payment error:', response.data?.message || 'Failed to load payment');
        setViewError(response.data?.message || 'Failed to load payment');
        setViewLoading(false);
      }
    } catch (error) {
      console.error('View EMI payment error:', error);
      setViewError(error.response?.data?.message || error.message || 'Failed to load payment');
      setViewLoading(false);
    }
  };

  // Fetch EMI payments
  const fetchEMIPayments = async (page = 1) => {
    try {
      setLoading(true);
      console.log('🔍 Fetching EMI payments...');
      const params = {
        page: page.toString(),
        limit: pagination.limit.toString(),
        transaction_type: 'emi_payment',
        ...filters
      };

      const response = await paymentTransactionsAPI.getAll(params);
      console.log('📊 EMI payments response:', response);
      
      // Enhance payment data with order details
      const enhancedPayments = await Promise.all(
        response.data.data.map(async (payment) => {
          if (payment.order_id) {
            try {
              const orderResponse = await orderAPI.getById(payment.order_id);
              if (orderResponse.data && orderResponse.data.data) {
                const orderData = orderResponse.data.data;
                return {
                  ...payment,
                  order_number: orderData.order_number,
                  order_status: orderData.order_status
                };
              }
            } catch (orderError) {
              console.warn(`Could not fetch order details for payment ${payment.id}:`, orderError.message);
            }
          }
          return payment;
        })
      );
      
      setEmiPayments(enhancedPayments);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('❌ Error fetching EMI payments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchEMIPayments();
  }, []);

  // Fetch orders, users, and payment methods when modal opens
  useEffect(() => {
    if (showPaymentModal || showCreateModal) {
      fetchOrders();
      fetchUsers();
      fetchActivePaymentMethods();
    }
  }, [showPaymentModal, showCreateModal]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && (showPaymentModal || showCreateModal)) {
        setShowPaymentModal(false);
        setShowCreateModal(false);
        setSelectedPayment(null);
        setSelectedOrderDetails(null);
        setSelectedUserDetails(null);
        setSearchOrder('');
        setSearchUser('');
        setInstallments([]);
        setShowInstallmentDetails(false);
      }
    };

    if (showPaymentModal || showCreateModal) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showPaymentModal, showCreateModal]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedPayment) {
        // Update existing payment
        await paymentTransactionsAPI.update(selectedPayment.id, formData);
        console.log('✅ EMI payment updated successfully');
      } else {
        // Create new payment
        await paymentTransactionsAPI.create(formData);
        console.log('✅ EMI payment created successfully');
      }
      
      // Reset form and close modals
      setFormData({
        order_id: '',
        user_id: '',
        transaction_type: 'emi_payment',
        amount: '',
        payment_method: '',
        payment_status: 'pending',
        transaction_id: '',
        payment_date: '',
        next_payment_date: '',
        next_payment_amount: '',
        total_installments: '',
        current_installment: '',
        remaining_amount: '',
        notes: ''
      });
      setShowPaymentModal(false);
      setShowCreateModal(false);
      setSelectedPayment(null);
      
      // Refresh the list
      fetchEMIPayments();
    } catch (error) {
      console.error('❌ Error saving EMI payment:', error);
      alert('Error saving EMI payment. Please try again.');
    }
  };

  // Handle edit payment
  const handleEdit = async (payment) => {
    console.log('🔍 Editing payment data:', payment);
    console.log('🔍 Next payment date:', payment.next_payment_date);
    console.log('🔍 Next payment amount:', payment.next_payment_amount);
    setSelectedPayment(payment);
    
    // Normalize payment method to match dropdown options
    const normalizePaymentMethod = (method) => {
      if (!method) return '';
      
      // Map common variations to standard values
      const methodMap = {
        'Cash On Delivery': 'cod',
        'Cash on Delivery': 'cod',
        'CASH ON DELIVERY': 'cod',
        'cash on delivery': 'cod',
        'Cashfree Payment': 'cashfree',
        'RAZORPAY': 'razorpay',
        'Razorpay Payment': 'razorpay',
        'PhonePe Payment': 'phonepe',
        'Offline Payment': 'offline'
      };
      
      return methodMap[method] || method.toLowerCase();
    };
    
    // Calculate next payment details if not available
    let nextPaymentDate = payment.next_payment_date ? payment.next_payment_date.split('T')[0] : '';
    let nextPaymentAmount = payment.next_payment_amount || '';
    
    // If next payment details are not available, calculate them
    if (!nextPaymentDate && payment.payment_date && payment.total_installments && payment.current_installment) {
      const currentDate = new Date(payment.payment_date);
      const currentInstallment = parseInt(payment.current_installment) || 1;
      const totalInstallments = parseInt(payment.total_installments) || 1;
      
      // Calculate next payment date (add 1 month from current payment date)
      const nextDate = new Date(currentDate);
      nextDate.setMonth(nextDate.getMonth() + 1);
      nextPaymentDate = nextDate.toISOString().split('T')[0];
    } else if (!nextPaymentDate && payment.created_at) {
      // Fallback: use created_at date and add 1 month
      const createdDate = new Date(payment.created_at);
      const nextDate = new Date(createdDate);
      nextDate.setMonth(nextDate.getMonth() + 1);
      nextPaymentDate = nextDate.toISOString().split('T')[0];
    }
    
    if (!nextPaymentAmount && payment.amount && payment.total_installments) {
      const totalAmount = parseFloat(payment.amount) || 0;
      const totalInstallments = parseInt(payment.total_installments) || 1;
      nextPaymentAmount = (totalAmount / totalInstallments).toFixed(2);
    }
    
    const formDataToSet = {
      order_id: payment.order_id || '',
      user_id: payment.user_id || '',
      transaction_type: 'emi_payment',
      amount: payment.amount || '',
      payment_method: normalizePaymentMethod(payment.payment_method),
      payment_status: payment.payment_status || 'pending',
      transaction_id: payment.transaction_id || '',
      payment_date: payment.payment_date ? payment.payment_date.split('T')[0] : '',
      next_payment_date: nextPaymentDate,
      next_payment_amount: nextPaymentAmount,
      total_installments: payment.total_installments || '',
      current_installment: payment.current_installment || '',
      remaining_amount: payment.remaining_amount || '',
      notes: payment.notes || ''
    };
    
    console.log('🔍 Calculated form data:', formDataToSet);
    console.log('🔍 Next payment date (calculated):', formDataToSet.next_payment_date);
    console.log('🔍 Next payment amount (calculated):', formDataToSet.next_payment_amount);
    
    setFormData(formDataToSet);
    
    // Pre-populate selected order and user details for display
    if (payment.order_id) {
      // Try to find order in the existing orders list first
      if (orders.length > 0) {
        const existingOrder = orders.find(order => order.id == payment.order_id);
        if (existingOrder) {
          setSelectedOrderDetails(existingOrder);
          setSearchOrder(`${existingOrder.order_number} - ${existingOrder.customer_name}`);
        } else {
          // Order not found in existing list, try API call
          try {
            const orderResponse = await orderAPI.getById(payment.order_id);
            if (orderResponse.data) {
              const orderData = orderResponse.data.data || orderResponse.data;
              setSelectedOrderDetails(orderData);
              setSearchOrder(`${orderData.order_number} - ${orderData.customer_name}`);
            }
          } catch (error) {
            console.warn('Could not fetch order details:', error.message);
            
            // Create fallback with payment data
            const fallbackOrder = {
              id: payment.order_id,
              order_number: payment.order_number || `Order #${payment.order_id}`,
              customer_name: payment.customer_name || payment.user_name || 'Customer Name Unavailable',
              customer_email: payment.customer_email || payment.user_email || 'Email Unavailable',
              total_amount: payment.amount || 0,
              order_status: 'completed', // Assume completed since payment exists
              created_at: payment.payment_date || new Date().toISOString()
            };
            
            setSelectedOrderDetails(fallbackOrder);
            setSearchOrder(`${fallbackOrder.order_number} - ${fallbackOrder.customer_name}`);
          }
        }
      } else {
        // No orders loaded yet, try API call
        try {
          const orderResponse = await orderAPI.getById(payment.order_id);
          if (orderResponse.data) {
            const orderData = orderResponse.data.data || orderResponse.data;
            setSelectedOrderDetails(orderData);
            setSearchOrder(`${orderData.order_number} - ${orderData.customer_name}`);
          }
        } catch (error) {
          console.warn('Could not fetch order details:', error.message);
          
          // Create fallback with payment data
          const fallbackOrder = {
            id: payment.order_id,
            order_number: payment.order_number || `Order #${payment.order_id}`,
            customer_name: payment.customer_name || payment.user_name || 'Customer Name Unavailable',
            customer_email: payment.customer_email || payment.user_email || 'Email Unavailable',
            total_amount: payment.amount || 0,
            order_status: 'completed', // Assume completed since payment exists
            created_at: payment.payment_date || new Date().toISOString()
          };
          
          setSelectedOrderDetails(fallbackOrder);
          setSearchOrder(`${fallbackOrder.order_number} - ${fallbackOrder.customer_name}`);
        }
      }
    }
    
    if (payment.user_id) {
      try {
        const userResponse = await userAPI.getById(payment.user_id);
        if (userResponse.data) {
          // Handle different possible response structures
          const userData = userResponse.data.user || userResponse.data.data || userResponse.data;
          setSelectedUserDetails(userData);
          
          // Check if userData has the user property (nested structure)
          const actualUser = userData.user || userData;
          
          if (actualUser.name && actualUser.email) {
            setSearchUser(`${actualUser.name} - ${actualUser.email}`);
          } else {
            setSearchUser(`User #${payment.user_id} - Details incomplete`);
          }
        }
      } catch (error) {
        console.warn('Could not fetch user details:', error.message);
        // Set a placeholder to show that a user is selected but details couldn't be loaded
        setSelectedUserDetails({ 
          id: payment.user_id, 
          name: `User #${payment.user_id}`, 
          email: 'User details unavailable'
        });
        setSearchUser(`User #${payment.user_id} - Details unavailable`);
      }
    }
    
    setShowPaymentModal(true);
  };

  // Handle delete payment
  const handleDelete = async (paymentId) => {
    if (window.confirm('Are you sure you want to delete this EMI payment?')) {
      try {
        await paymentTransactionsAPI.delete(paymentId);
        console.log('✅ EMI payment deleted successfully');
        fetchEMIPayments();
      } catch (error) {
        console.error('❌ Error deleting EMI payment:', error);
        alert('Error deleting EMI payment. Please try again.');
      }
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Get status badge using utility function
  const getStatusBadgeComponent = (status) => {
    const badgeConfig = getStatusBadge(status, 'payment');
    const IconComponent = {
      'Clock': Clock,
      'CheckCircle': CheckCircle,
      'XCircle': XCircle,
      'AlertCircle': AlertCircle
    }[badgeConfig.icon] || Clock;

    return (
      <span className={badgeConfig.className}>
        <IconComponent className="w-3 h-3 mr-1" />
        {badgeConfig.displayText}
      </span>
    );
  };

  // Fetch orders for dropdown
  const fetchOrders = async (search = '') => {
    setLoadingOrders(true);
    try {
      const params = { 
        limit: 20,
        search: search.trim(),
        sortBy: 'created_at',
        sortOrder: 'desc'
      };
      const response = await orderAPI.getAll(params);
      
      // Parse response data with multiple structure support
      let ordersData = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        ordersData = response.data.data;
      } else if (response.data?.orders && Array.isArray(response.data.orders)) {
        ordersData = response.data.orders;
      } else if (Array.isArray(response.data)) {
        ordersData = response.data;
      } else if (response.orders && Array.isArray(response.orders)) {
        ordersData = response.orders;
      } else if (response.data?.data && typeof response.data.data === 'object') {
        // Handle nested structure
        if (response.data.data.orders && Array.isArray(response.data.data.orders)) {
          ordersData = response.data.data.orders;
        } else if (response.data.data.data && Array.isArray(response.data.data.data)) {
          ordersData = response.data.data.data;
        }
      }
      
      setOrders(ordersData);
      
      // Fallback: try without search if no results found
      if (ordersData.length === 0 && search.trim()) {
        const fallbackParams = { 
          limit: 20,
          sortBy: 'created_at',
          sortOrder: 'desc'
        };
        const fallbackResponse = await orderAPI.getAll(fallbackParams);
        
        let fallbackOrdersData = [];
        if (fallbackResponse.data?.data && Array.isArray(fallbackResponse.data.data)) {
          fallbackOrdersData = fallbackResponse.data.data;
        } else if (fallbackResponse.data?.orders && Array.isArray(fallbackResponse.data.orders)) {
          fallbackOrdersData = fallbackResponse.data.orders;
        } else if (Array.isArray(fallbackResponse.data)) {
          fallbackOrdersData = fallbackResponse.data;
        }
        
        if (fallbackOrdersData.length > 0) {
          setOrders(fallbackOrdersData);
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Fetch users for dropdown
  const fetchUsers = async (search = '') => {
    setLoadingUsers(true);
    try {
      const params = { 
        limit: 20,
        search: search.trim(),
        sortBy: 'created_at',
        sortOrder: 'desc'
      };
      const response = await userAPI.getAll(params);
      
      // Parse response data with multiple structure support
      let usersData = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        usersData = response.data.data;
      } else if (response.data?.users && Array.isArray(response.data.users)) {
        usersData = response.data.users;
      } else if (Array.isArray(response.data)) {
        usersData = response.data;
      } else if (response.users && Array.isArray(response.users)) {
        usersData = response.users;
      } else if (response.data?.data && typeof response.data.data === 'object') {
        // Handle nested structure
        if (response.data.data.users && Array.isArray(response.data.data.users)) {
          usersData = response.data.data.users;
        } else if (response.data.data.data && Array.isArray(response.data.data.data)) {
          usersData = response.data.data.data;
        }
      }
      
      setUsers(usersData);
      
      // Fallback: try without search if no results found
      if (usersData.length === 0 && search.trim()) {
        const fallbackParams = { 
          limit: 20,
          sortBy: 'created_at',
          sortOrder: 'desc'
        };
        const fallbackResponse = await userAPI.getAll(fallbackParams);
        
        let fallbackUsersData = [];
        if (fallbackResponse.data?.data && Array.isArray(fallbackResponse.data.data)) {
          fallbackUsersData = fallbackResponse.data.data;
        } else if (fallbackResponse.data?.users && Array.isArray(fallbackResponse.data.users)) {
          fallbackUsersData = fallbackResponse.data.users;
        } else if (Array.isArray(fallbackResponse.data)) {
          fallbackUsersData = fallbackResponse.data;
        }
        
        if (fallbackUsersData.length > 0) {
          setUsers(fallbackUsersData);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Handle order selection
  const handleOrderSelect = (order) => {
    setFormData(prev => ({
      ...prev,
      order_id: order.id,
      user_id: order.user_id,
      amount: order.total_amount || ''
    }));
    setSelectedOrderDetails(order);
    
    // Auto-select the user if order has user_id
    if (order.user_id) {
      const user = users.find(u => u.id === order.user_id);
      if (user) {
        setSelectedUserDetails(user);
      }
    }
    setSearchOrder(`${order.order_number} - ${order.customer_name}`);
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    setFormData(prev => ({
      ...prev,
      user_id: user.id
    }));
    setSelectedUserDetails(user);
    setSearchUser(`${user.name} - ${user.email}`);
  };

  // Fetch active payment methods
  const fetchActivePaymentMethods = async () => {
    setLoadingPaymentMethods(true);
    try {
      const response = await paymentMethodsAPI.getActiveMethods();
      if (response.data.success) {
        const activeMethods = response.data.data.methods || [];
        
        // Map database method names to display format
        const mappedMethods = activeMethods
          .filter(method => paymentMethodsMapping[method]) // Only include methods we have mapping for
          .map(method => ({
            value: method,
            label: paymentMethodsMapping[method].label,
            icon: paymentMethodsMapping[method].icon
          }));
        
        setPaymentMethods(mappedMethods);
        console.log('✅ Active payment methods loaded:', mappedMethods);
      } else {
        console.error('❌ Error loading payment methods:', response.data.message);
        setPaymentMethods([]);
      }
    } catch (error) {
      console.error('❌ Error fetching active payment methods:', error);
      setPaymentMethods([]);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  // Handle payment method selection
  const handlePaymentMethodSelect = (method) => {
    setFormData(prev => ({
      ...prev,
      payment_method: method.value
    }));
  };

  // Generate installments based on total installments and amount
  const generateInstallments = (totalInstallments, totalAmount, orderDate = null) => {
    if (!totalInstallments || !totalAmount || totalInstallments <= 0) {
      setInstallments([]);
      setShowInstallmentDetails(false);
      return;
    }

    const amount = parseFloat(totalAmount);
    const installmentCount = parseInt(totalInstallments);
    
    // Calculate base installment amount
    const baseAmount = Math.floor((amount / installmentCount) * 100) / 100; // Round to 2 decimals
    const remainder = Math.round((amount - (baseAmount * installmentCount)) * 100) / 100;
    
    const newInstallments = [];
    const startDate = orderDate ? new Date(orderDate) : new Date();
    
    for (let i = 0; i < installmentCount; i++) {
      // Add remainder to the last installment to ensure total matches
      const installmentAmount = i === installmentCount - 1 
        ? baseAmount + remainder 
        : baseAmount;
      
      // Calculate installment date (monthly intervals)
      const installmentDate = new Date(startDate);
      installmentDate.setMonth(startDate.getMonth() + i + 1); // Next month from order date
      
      newInstallments.push({
        installment_number: i + 1,
        amount: installmentAmount.toFixed(2),
        due_date: installmentDate.toISOString().split('T')[0], // YYYY-MM-DD format
        status: 'pending'
      });
    }
    
    setInstallments(newInstallments);
    setShowInstallmentDetails(true);
  };

  // Handle installment amount change
  const handleInstallmentAmountChange = (index, newAmount) => {
    setInstallments(prev => prev.map((installment, i) => 
      i === index 
        ? { ...installment, amount: newAmount }
        : installment
    ));
  };

  // Handle installment date change
  const handleInstallmentDateChange = (index, newDate) => {
    setInstallments(prev => prev.map((installment, i) => 
      i === index 
        ? { ...installment, due_date: newDate }
        : installment
    ));
  };

  // Auto-generate installments when total installments or amount changes
  const handleInstallmentInputChange = (e) => {
    const { name, value } = e.target;
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Auto-generate installments if both total_installments and amount are available
    const updatedData = { ...formData, [name]: value };
    if (updatedData.total_installments && updatedData.amount) {
      const orderDate = selectedOrderDetails?.created_at || updatedData.payment_date;
      generateInstallments(
        updatedData.total_installments, 
        updatedData.amount,
        orderDate
      );
    }
  };

  // Currency formatting helper to match Orders details view
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const formatDateSafe = (value, withTime = false) => {
    if (!value) return 'N/A';
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return 'N/A';
      return withTime ? d.toLocaleString() : d.toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            EMI Payments
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Manage EMI payment transactions and track installment status
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          <button
            onClick={() => {
              setSelectedPayment(null);
              setFormData({
                order_id: '',
                user_id: '',
                transaction_type: 'emi_payment',
                amount: '',
                payment_method: '',
                payment_status: 'pending',
                transaction_id: '',
                payment_date: '',
                next_payment_date: '',
                next_payment_amount: '',
                total_installments: '',
                current_installment: '',
                remaining_amount: '',
                notes: ''
              });
              setSelectedOrderDetails(null);
              setSelectedUserDetails(null);
              setSearchOrder('');
              setSearchUser('');
              setInstallments([]);
              setShowInstallmentDetails(false);
              setShowCreateModal(true);
            }}
            className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Add EMI Payment</span>
            <span className="sm:hidden">Add EMI</span>
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Filter className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Filters</span>
            <span className="sm:hidden">Filter</span>
            {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
          </button>
          <button
            onClick={() => fetchEMIPayments()}
            className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Refresh</span>
            <span className="sm:hidden">Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Search
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Search by order number, user name, or email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Payment Status
              </label>
              <select
                value={filters.payment_status}
                onChange={(e) => setFilters(prev => ({ ...prev, payment_status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Payment Statuses</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
                <option value="coming_soon">Coming Soon</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Installment Status
              </label>
              <select
                value={filters.installment_status}
                onChange={(e) => setFilters(prev => ({ ...prev, installment_status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Installment Statuses</option>
                <option value="coming_soon">Coming Soon</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                User ID
              </label>
              <input
                type="text"
                value={filters.user_id}
                onChange={(e) => setFilters(prev => ({ ...prev, user_id: e.target.value }))}
                placeholder="Filter by user ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={() => setFilters({
                payment_status: '',
                installment_status: '',
                user_id: '',
                order_id: '',
                search: ''
              })}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Clear
            </button>
            <button
              onClick={() => fetchEMIPayments(1)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* EMI Payments Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  EMI Payment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Installments
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Next Payment
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    Loading EMI payments...
                  </td>
                </tr>
              ) : emiPayments.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    No EMI payments found
                  </td>
                </tr>
              ) : (
                emiPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center">
                            <CreditCard className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            #{payment.id}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            EMI Payment
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {payment.order_number || `Order #${payment.order_id}`}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {mapPaymentMethodToDisplay(payment.payment_method)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {payment.user_name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {payment.user_email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        ₹{payment.amount}
                      </div>
                      {payment.remaining_amount && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Remaining: ₹{payment.remaining_amount}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {payment.current_installment || 0} / {payment.total_installments || 0}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Installment
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {getStatusBadgeComponent(payment.payment_status)}
                        {payment.installment_status && payment.installment_status !== payment.payment_status && (
                          <div className="text-xs text-gray-500">
                            Installment: {mapPaymentStatusToDisplay(payment.installment_status)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {payment.next_payment_date ? (
                        <div>
                          <div className="text-sm text-gray-900 dark:text-white">
                            {new Date(payment.next_payment_date).toLocaleDateString()}
                          </div>
                          {payment.next_payment_amount && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              ₹{payment.next_payment_amount}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => viewPayment(payment.id)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(payment)}
                          className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                          title="Edit Payment"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(payment.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete Payment"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
                 </div>
       </div>

       {/* Enhanced EMI Payment Modal */}
       {(showPaymentModal || showCreateModal) && createPortal(
         <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-md flex items-start justify-center z-[9999] p-2 sm:p-4 overflow-y-auto animate-in fade-in duration-300">
           <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl shadow-2xl drop-shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-6xl my-4 sm:my-8 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col animate-in slide-in-from-top-4 duration-300">
             {/* Modal Header */}
             <div className="bg-gradient-to-r from-purple-600 to-purple-700 dark:from-purple-700 dark:to-purple-800 p-4 sm:p-6 rounded-t-lg sm:rounded-t-xl flex-shrink-0">
               <div className="flex items-center justify-between">
                 <div className="flex items-center space-x-3">
                   <div className="bg-white dark:bg-gray-800 p-2 rounded-lg">
                     <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-400" />
                   </div>
                   <div>
                     <h2 className="text-xl font-bold text-white">
                       {selectedPayment ? 'Edit EMI Payment' : 'Create New EMI Payment'}
                     </h2>
                     <p className="text-purple-100 text-sm">
                       {selectedPayment ? 'Update payment details' : 'Add a new EMI payment transaction'}
                     </p>
                   </div>
                 </div>
                 <button
                   onClick={() => {
                     setShowPaymentModal(false);
                     setShowCreateModal(false);
                     setSelectedPayment(null);
                     setSelectedOrderDetails(null);
                     setSelectedUserDetails(null);
                     setSearchOrder('');
                     setSearchUser('');
                     setInstallments([]);
                     setShowInstallmentDetails(false);
                   }}
                   className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                 >
                   <X className="h-5 w-5" />
                 </button>
               </div>
             </div>

                               <form onSubmit={handleSubmit} className="p-4 sm:p-6 overflow-y-auto flex-1">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:p-6">
                 {/* Left Column - Order Selection */}
                 <div className="lg:col-span-1 space-y-6">
                   <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-4 sm:p-6 rounded-xl border border-blue-200 dark:border-blue-700">
                     <div className="flex items-center space-x-3 mb-4">
                       <ShoppingCart className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                       <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Order Selection</h3>
                     </div>
                     
                     {/* Order Search */}
                     <div className="space-y-3">
                       <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                         Search & Select Order *
                       </label>
                       <div className="relative">
                         <input
                           type="text"
                           placeholder="Search by order number or customer name..."
                           value={searchOrder}
                           onChange={(e) => {
                             setSearchOrder(e.target.value);
                             if (e.target.value.length >= 2) {
                               fetchOrders(e.target.value);
                             }
                           }}
                           className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                           required
                         />
                         <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                       </div>
                       
                       {/* Order Dropdown */}
                       {searchOrder && !selectedOrderDetails && (
                         <div className="mt-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg max-h-60 overflow-y-auto">
                           {loadingOrders ? (
                             <div className="p-3 text-center text-gray-500">Loading orders...</div>
                           ) : orders.length > 0 ? (
                             orders.map((order) => (
                               <div
                                 key={order.id}
                                 onClick={() => handleOrderSelect(order)}
                                 className="p-3 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-0"
                               >
                                 <div className="flex justify-between items-start">
                                   <div>
                                     <p className="font-medium text-gray-900 dark:text-white">#{order.order_number}</p>
                                     <p className="text-sm text-gray-600 dark:text-gray-400">{order.customer_name}</p>
                                     <p className="text-xs text-gray-500">{order.customer_email}</p>
                                   </div>
                                   <div className="text-right">
                                     <p className="font-semibold text-purple-600 dark:text-purple-400">
                                       ₹{Number(order.total_amount || 0).toLocaleString()}
                                     </p>
                                     <span className={`text-xs px-2 py-1 rounded-full ${
                                       order.order_status === 'completed' ? 'bg-green-100 text-green-800' :
                                       order.order_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                       'bg-gray-100 text-gray-800'
                                     }`}>
                                       {order.order_status}
                                     </span>
                                   </div>
                                 </div>
                               </div>
                             ))
                           ) : (
                             <div className="p-3 text-center text-gray-500">No orders found</div>
                           )}
                         </div>
                       )}
                       
                       {/* Selected Order Display */}
                       {selectedOrderDetails && (
                         <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                           <div className="flex justify-between items-start">
                             <div>
                               <p className="font-semibold text-blue-900 dark:text-blue-100">#{selectedOrderDetails.order_number}</p>
                               <p className="text-sm text-blue-700 dark:text-blue-300">{selectedOrderDetails.customer_name}</p>
                               <p className="text-xs text-blue-600 dark:text-blue-400">{selectedOrderDetails.customer_email}</p>
                             </div>
                             <div className="text-right">
                               <p className="font-bold text-blue-900 dark:text-blue-100">
                                 ₹{Number(selectedOrderDetails.total_amount || 0).toLocaleString()}
                               </p>
                               <button
                                 type="button"
                                 onClick={() => {
                                   setSelectedOrderDetails(null);
                                   setFormData(prev => ({ ...prev, order_id: '', user_id: '', amount: '' }));
                                   setSearchOrder('');
                                 }}
                                 className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                               >
                                 Change
                               </button>
                             </div>
                           </div>
                         </div>
                       )}
                     </div>
                   </div>

                   {/* User Selection */}
                   <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-4 sm:p-6 rounded-xl border border-green-200 dark:border-green-700">
                     <div className="flex items-center space-x-3 mb-4">
                       <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
                       <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Customer Selection</h3>
                     </div>
                     
                     {/* User Search */}
                     <div className="space-y-3">
                       <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                         Search & Select Customer *
                       </label>
                       <div className="relative">
                         <input
                           type="text"
                           placeholder="Search by name or email..."
                           value={searchUser}
                           onChange={(e) => {
                             setSearchUser(e.target.value);
                             if (e.target.value.length >= 2) {
                               fetchUsers(e.target.value);
                             }
                           }}
                           className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                           required
                         />
                         <Users className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                       </div>
                       
                       {/* User Dropdown */}
                       {searchUser && !selectedUserDetails && (
                         <div className="mt-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg max-h-60 overflow-y-auto">
                           {loadingUsers ? (
                             <div className="p-3 text-center text-gray-500">Loading users...</div>
                           ) : users.length > 0 ? (
                             users.map((user) => (
                               <div
                                 key={user.id}
                                 onClick={() => handleUserSelect(user)}
                                 className="p-3 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-0"
                               >
                                 <div className="flex items-center space-x-3">
                                   <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                                     <User className="h-4 w-4 text-green-600 dark:text-green-400" />
                                   </div>
                                   <div>
                                     <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                                     <p className="text-sm text-gray-600 dark:text-gray-400">{user.email}</p>
                                     {user.phone && (
                                       <p className="text-xs text-gray-500">{user.phone}</p>
                                     )}
                                   </div>
                                 </div>
                               </div>
                             ))
                           ) : (
                             <div className="p-3 text-center text-gray-500">No users found</div>
                           )}
                         </div>
                       )}
                       
                       {/* Selected User Display */}
                       {selectedUserDetails && (
                         <div className="mt-3 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg">
                           <div className="flex justify-between items-start">
                             <div className="flex items-center space-x-3">
                               <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                                 <User className="h-5 w-5 text-green-600 dark:text-green-400" />
                               </div>
                               <div>
                                 <p className="font-semibold text-green-900 dark:text-green-100">
                                   {selectedUserDetails.user ? selectedUserDetails.user.name : selectedUserDetails.name}
                                 </p>
                                 <p className="text-sm text-green-700 dark:text-green-300">
                                   {selectedUserDetails.user ? selectedUserDetails.user.email : selectedUserDetails.email}
                                 </p>
                                 {(selectedUserDetails.user ? selectedUserDetails.user.phone : selectedUserDetails.phone) && (
                                   <p className="text-xs text-green-600 dark:text-green-400">
                                     {selectedUserDetails.user ? selectedUserDetails.user.phone : selectedUserDetails.phone}
                                   </p>
                                 )}
                               </div>
                             </div>
                             <button
                               type="button"
                               onClick={() => {
                                 setSelectedUserDetails(null);
                                 setFormData(prev => ({ ...prev, user_id: '' }));
                                 setSearchUser('');
                               }}
                               className="text-xs text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                             >
                               Change
                             </button>
                           </div>
                         </div>
                       )}
                     </div>
                   </div>
                 </div>

                 {/* Right Column - Payment Details */}
                 <div className="lg:col-span-2 space-y-6">
                   {/* Payment Information */}
                   <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-4 sm:p-6 rounded-xl border border-purple-200 dark:border-purple-700">
                     <div className="flex items-center space-x-3 mb-6">
                       <IndianRupee className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                       <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payment Information</h3>
                     </div>
                     
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                       {/* Amount */}
                       <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           EMI Amount *
                         </label>
                         <div className="relative">
                           <IndianRupee className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                                    <input
                           type="number"
                           name="amount"
                           step="0.01"
                           value={formData.amount}
                           onChange={handleInstallmentInputChange}
                           className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                           placeholder="0.00"
                           required
                         />
                         </div>
                       </div>

                       {/* Payment Method */}
                       <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Payment Method *
                         </label>
                         {!loadingPaymentMethods && paymentMethods.length === 0 && (
                           <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                             <p className="text-xs text-yellow-700 dark:text-yellow-300">
                               ⚠️ No active payment methods found. Please configure payment methods in the admin panel first.
                             </p>
                           </div>
                         )}
                         <div className="relative">
                           <select
                             name="payment_method"
                             value={formData.payment_method}
                             onChange={handleInputChange}
                             className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white appearance-none"
                             required
                             disabled={loadingPaymentMethods}
                             
                           >
                             <option value="">
                               {loadingPaymentMethods ? 'Loading payment methods...' : 'Select Payment Method'}
                             </option>
                                                            {paymentMethods.map((method) => (
                                 <option key={method.value} value={method.value}>
                                   {method.label}
                                 </option>
                               ))}
                             {!loadingPaymentMethods && paymentMethods.length === 0 && (
                               <option value="" disabled>No active payment methods found</option>
                             )}
                           </select>
                           <PaymentIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                           {loadingPaymentMethods ? (
                             <RefreshCw className="absolute right-3 top-3 h-5 w-5 text-gray-400 animate-spin" />
                           ) : (
                             <ChevronDown className="absolute right-3 top-3 h-5 w-5 text-gray-400" />
                           )}
                         </div>
                       </div>

                       {/* Payment Status */}
                       <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Payment Status
                         </label>
                         <select
                           name="payment_status"
                           value={formData.payment_status}
                           onChange={handleInputChange}
                           className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                         >
                           <option value="pending">Pending</option>
                           <option value="completed">Completed</option>
                           <option value="failed">Failed</option>
                           <option value="refunded">Refunded</option>
                           <option value="processing">Processing</option>
                         </select>
                       </div>

                       {/* Total Installments */}
                       <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Total Installments
                         </label>
                         <input
                           type="number"
                           name="total_installments"
                           value={formData.total_installments}
                           onChange={handleInstallmentInputChange}
                           className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                           placeholder="12"
                           min="1"
                           max="60"
                         />
                       </div>

                       {/* Current Installment */}
                       <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Current Installment
                         </label>
                         <input
                           type="number"
                           name="current_installment"
                           value={formData.current_installment}
                           onChange={handleInputChange}
                           className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                           placeholder="1"
                           min="1"
                         />
                       </div>

                       {/* Remaining Amount */}
                       <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Remaining Amount
                         </label>
                         <div className="relative">
                           <IndianRupee className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                           <input
                             type="number"
                             name="remaining_amount"
                             step="0.01"
                             value={formData.remaining_amount}
                             onChange={handleInputChange}
                             className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                             placeholder="0.00"
                           />
                         </div>
                       </div>

                       {/* Payment Date */}
                       <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Payment Date
                         </label>
                         <input
                           type="date"
                           name="payment_date"
                           value={formData.payment_date}
                           onChange={handleInputChange}
                           className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                         />
                       </div>

                       {/* Transaction ID */}
                       <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Transaction ID
                         </label>
                         <input
                           type="text"
                           name="transaction_id"
                           value={formData.transaction_id}
                           onChange={handleInputChange}
                           className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                           placeholder="Enter transaction ID"
                         />
                       </div>
                     </div>
                   </div>

                                       {/* Schedule Information */}
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 p-4 sm:p-6 rounded-xl border border-orange-200 dark:border-orange-700">
                      <div className="flex items-center space-x-3 mb-6">
                        <Calendar className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Schedule Information</h3>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                        {/* Next Payment Date */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Next Payment Date
                          </label>
                          <input
                            type="date"
                            name="next_payment_date"
                            value={formData.next_payment_date}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white"
                          />
                        </div>

                        {/* Next Payment Amount */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Next Payment Amount
                          </label>
                          <div className="relative">
                            <IndianRupee className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                            <input
                              type="number"
                              name="next_payment_amount"
                              step="0.01"
                              value={formData.next_payment_amount}
                              onChange={handleInputChange}
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                   {/* Notes */}
                   <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900/20 dark:to-gray-800/20 p-4 sm:p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                     <div className="flex items-center space-x-3 mb-4">
                       <FileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                       <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Additional Notes</h3>
                     </div>
                     
                     <textarea
                       name="notes"
                       value={formData.notes}
                       onChange={handleInputChange}
                       rows="4"
                       className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-white resize-none"
                       placeholder="Add any additional notes or comments about this EMI payment..."
                     />
                   </div>
                 </div>
               </div>

               {/* Installments Details Section */}
               {showInstallmentDetails && installments.length > 0 && (
                 <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 p-4 sm:p-6 rounded-xl border border-indigo-200 dark:border-indigo-700">
                   <div className="flex items-center justify-between mb-6">
                     <div className="flex items-center space-x-3">
                       <Calendar className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                       <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Installment Details</h3>
                     </div>
                     <div className="text-sm text-indigo-600 dark:text-indigo-400">
                       Total: ₹{installments.reduce((sum, inst) => sum + parseFloat(inst.amount), 0).toFixed(2)}
                     </div>
                   </div>
                   
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     {installments.map((installment, index) => (
                       <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-indigo-200 dark:border-indigo-700 shadow-sm">
                         <div className="flex items-center justify-between mb-3">
                           <h4 className="font-medium text-gray-900 dark:text-white">
                             {installment.installment_number}{installment.installment_number === 1 ? 'st' : 
                              installment.installment_number === 2 ? 'nd' : 
                              installment.installment_number === 3 ? 'rd' : 'th'} Installment
                           </h4>
                           <span className={`text-xs px-2 py-1 rounded-full ${
                             installment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                             installment.status === 'paid' ? 'bg-green-100 text-green-800' :
                             'bg-gray-100 text-gray-800'
                           }`}>
                             {installment.status}
                           </span>
                         </div>
                         
                         <div className="space-y-3">
                           <div>
                             <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                               Amount (₹)
                             </label>
                             <div className="relative">
                               <IndianRupee className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
                               <input
                                 type="number"
                                 step="0.01"
                                 value={installment.amount}
                                 onChange={(e) => handleInstallmentAmountChange(index, e.target.value)}
                                 className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                                 placeholder="0.00"
                               />
                             </div>
                           </div>
                           
                           <div>
                             <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                               Due Date
                             </label>
                             <input
                               type="date"
                               value={installment.due_date}
                               onChange={(e) => handleInstallmentDateChange(index, e.target.value)}
                               className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                             />
                           </div>
                         </div>
                       </div>
                     ))}
                   </div>
                   
                   <div className="mt-4 flex items-center justify-between p-3 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-lg">
                     <div className="flex items-center space-x-2 text-sm text-indigo-700 dark:text-indigo-300">
                       <AlertCircle className="h-4 w-4" />
                       <span>
                         You can modify individual installment amounts and dates.
                       </span>
                     </div>
                     <button
                       type="button"
                       onClick={() => generateInstallments(
                         formData.total_installments, 
                         formData.amount,
                         selectedOrderDetails?.created_at || formData.payment_date
                       )}
                       className="text-xs px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                     >
                       Regenerate
                     </button>
                   </div>
                 </div>
               )}

               {/* Action Buttons */}
               <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                 <button
                   type="button"
                   onClick={() => {
                     setShowPaymentModal(false);
                     setShowCreateModal(false);
                     setSelectedPayment(null);
                     setSelectedOrderDetails(null);
                     setSelectedUserDetails(null);
                     setSearchOrder('');
                     setSearchUser('');
                     setInstallments([]);
                     setShowInstallmentDetails(false);
                   }}
                   className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                 >
                   Cancel
                 </button>
                 <button
                   type="submit"
                   className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200 shadow-lg hover:shadow-xl"
                 >
                   {selectedPayment ? 'Update EMI Payment' : 'Create EMI Payment'}
                 </button>
               </div>
             </form>
           </div>
         </div>,
         document.body
       )}



             {/* EMI Payment Details View Modal */}
      {showViewModal && selectedPayment && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
             <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
               <div className="flex items-center justify-between">
                 <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                   EMI Payment Details - #{selectedPayment.id}
                   {selectedPayment.order_number && (
                     <span className="text-sm font-normal text-gray-600 dark:text-gray-400 ml-2">(Order: {selectedPayment.order_number})</span>
                   )}
                 </h2>
                 <div className="flex items-center space-x-2">
                   <button
                     onClick={() => {
                       setShowViewModal(false);
                       setSelectedPayment(null);
                     }}
                     className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                   >
                     <X className="h-5 w-5" />
                   </button>
                 </div>
               </div>
             </div>

             <div className="p-4 sm:p-6 space-y-6">
                {viewLoading && (
                  <div className="text-center text-gray-600 dark:text-gray-300">Loading payment details...</div>
                )}
                {viewError && (
                  <div className="text-center text-red-600 dark:text-red-400">{viewError}</div>
                )}
               {/* Payment Summary */}
               <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                 <div>
                   <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Order Status</p>
                   <p className={`text-lg font-semibold ${getStatusColor(selectedPayment.order_status)}`}>
                                             {mapOrderStatusToDisplay(selectedPayment.order_status)}
                   </p>
                 </div>
                 <div>
                   <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Payment Status</p>
                   <p className={`text-lg font-semibold ${getStatusColor(selectedPayment.payment_status)}`}>
                     {selectedPayment.payment_status || 'Unknown'}
                   </p>
                 </div>
                 <div>
                   <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Amount</p>
                   <p className="text-lg font-semibold text-gray-900 dark:text-white">
                     {formatCurrency(selectedPayment.amount)}
                   </p>
                 </div>
               </div>

               {/* Payment Option Details */}
               {selectedPayment.payment_option && selectedPayment.payment_option !== 'full' && (
                 <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                   <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3">Payment Option Details</h4>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                       <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Payment Type</p>
                       <p className={`text-lg font-semibold ${
                         selectedPayment.payment_option === 'advance' 
                           ? 'text-blue-600 dark:text-blue-400' 
                           : selectedPayment.payment_option === 'emi'
                           ? 'text-purple-600 dark:text-purple-400'
                           : 'text-green-600 dark:text-green-400'
                       }`}>
                         {selectedPayment.payment_option === 'advance' ? 'Split Payment' : 
                          selectedPayment.payment_option === 'emi' ? 'EMI Payment' : 'Full Payment'}
                       </p>
                     </div>
                     <div>
                       <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Payment Method</p>
                       <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                         {mapPaymentMethodToDisplay(selectedPayment.payment_method) || 'N/A'}
                       </p>
                     </div>
                     {selectedPayment.next_payment_amount && (
                       <div>
                         <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Next Payment Amount</p>
                         <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                           ₹{selectedPayment.next_payment_amount}
                         </p>
                       </div>
                     )}
                     {selectedPayment.next_payment_date && (
                       <div>
                         <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Next Payment Date</p>
                         <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">{formatDateSafe(selectedPayment.next_payment_date)}</p>
                       </div>
                     )}
                     {selectedPayment.remaining_amount && (
                       <div>
                         <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Remaining Amount</p>
                         <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                           ₹{selectedPayment.remaining_amount}
                         </p>
                       </div>
                     )}
                     {selectedPayment.total_installments && (
                       <div>
                         <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Installments</p>
                         <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                           {selectedPayment.total_installments}
                         </p>
                       </div>
                     )}
                     {selectedPayment.current_installment && (
                       <div>
                         <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Current Installment</p>
                         <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                           {selectedPayment.current_installment}
                         </p>
                       </div>
                     )}
                   </div>
                   
                   {/* Payment Option Instructions */}
                   {selectedPayment.payment_option === 'emi' && (
                     <div className="mt-3 p-3 bg-purple-100 dark:bg-purple-800/30 rounded-md">
                       <p className="text-sm text-purple-800 dark:text-purple-200">
                         Your EMI payment plan details are shown below. Please ensure timely payments.
                       </p>
                       
                       {/* EMI Details */}
                       {selectedPayment.payment_option_details && (
                         <div className="mt-3 space-y-2">
                           {(() => {
                             const details = selectedPayment.payment_option_details;
                             
                             // Handle string data (try to parse JSON)
                             if (typeof details === 'string') {
                               try {
                                 const parsedDetails = JSON.parse(details);
                                 const emiDetails = parsedDetails.emi_details || {};
                                 return (
                                   <div className="space-y-2">
                                     {emiDetails.selectedEmiTenure && (
                                       <div className="flex justify-between text-sm">
                                         <span className="text-purple-700 dark:text-purple-300">EMI Tenure:</span>
                                         <span className="text-purple-900 dark:text-purple-100 font-medium">
                                           {emiDetails.selectedEmiTenure} months
                                         </span>
                                       </div>
                                     )}
                                     {emiDetails.emiAmount && (
                                       <div className="flex justify-between text-sm">
                                         <span className="text-purple-700 dark:text-purple-300">Monthly EMI:</span>
                                         <span className="text-purple-900 dark:text-purple-100 font-medium">
                                           ₹{emiDetails.emiAmount}
                                         </span>
                                       </div>
                                     )}
                                     {emiDetails.emiPrincipal && (
                                       <div className="flex justify-between text-sm">
                                         <span className="text-purple-700 dark:text-purple-300">EMI Principal:</span>
                                         <span className="text-purple-900 dark:text-purple-100 font-medium">
                                           ₹{emiDetails.emiPrincipal}
                                         </span>
                                       </div>
                                     )}
                                     {emiDetails.totalEmiAmount && (
                                       <div className="flex justify-between text-sm">
                                         <span className="text-purple-700 dark:text-purple-300">Total EMI Amount:</span>
                                         <span className="text-purple-900 dark:text-purple-100 font-medium">
                                           ₹{emiDetails.totalEmiAmount}
                                         </span>
                                       </div>
                                     )}
                                     {parsedDetails.paymentOptionsConfig?.emi_interest_rate && (
                                       <div className="flex justify-between text-sm">
                                         <span className="text-purple-700 dark:text-purple-300">Interest Rate:</span>
                                         <span className="text-purple-900 dark:text-purple-100 font-medium">
                                           {parsedDetails.paymentOptionsConfig.emi_interest_rate}% p.a.
                                         </span>
                                       </div>
                                     )}
                                   </div>
                                 );
                               } catch (e) {
                                 return <div className="text-purple-700 dark:text-purple-300 text-sm">EMI details not available</div>;
                               }
                             }
                             
                             // Handle object data
                             if (typeof details === 'object' && details !== null) {
                               const emiDetails = details.emi_details || {};
                               return (
                                 <div className="space-y-2">
                                   {emiDetails.selectedEmiTenure && (
                                     <div className="flex justify-between text-sm">
                                       <span className="text-purple-700 dark:text-purple-300">EMI Tenure:</span>
                                       <span className="text-purple-900 dark:text-purple-100 font-medium">
                                         {emiDetails.selectedEmiTenure} months
                                       </span>
                                     </div>
                                   )}
                                   {emiDetails.emiAmount && (
                                     <div className="flex justify-between text-sm">
                                       <span className="text-purple-700 dark:text-purple-300">Monthly EMI:</span>
                                       <span className="text-purple-900 dark:text-purple-100 font-medium">
                                         ₹{emiDetails.emiAmount}
                                       </span>
                                     </div>
                                   )}
                                   {emiDetails.emiPrincipal && (
                                     <div className="flex justify-between text-sm">
                                       <span className="text-purple-700 dark:text-purple-300">EMI Principal:</span>
                                       <span className="text-purple-900 dark:text-purple-100 font-medium">
                                         ₹{emiDetails.emiPrincipal}
                                       </span>
                                     </div>
                                   )}
                                   {emiDetails.totalEmiAmount && (
                                     <div className="flex justify-between text-sm">
                                       <span className="text-purple-700 dark:text-purple-300">Total EMI Amount:</span>
                                       <span className="text-purple-900 dark:text-purple-100 font-medium">
                                         ₹{emiDetails.totalEmiAmount}
                                       </span>
                                     </div>
                                   )}
                                   {details.paymentOptionsConfig?.emi_interest_rate && (
                                     <div className="flex justify-between text-sm">
                                       <span className="text-purple-700 dark:text-purple-300">Interest Rate:</span>
                                       <span className="text-purple-900 dark:text-purple-100 font-medium">
                                         {details.paymentOptionsConfig.emi_interest_rate}% p.a.
                                       </span>
                                     </div>
                                   )}
                                 </div>
                               );
                             }
                             
                             return <div className="text-purple-700 dark:text-purple-300 text-sm">EMI details not available</div>;
                           })()}
                         </div>
                       )}
                     </div>
                   )}

                 </div>
               )}

               {/* Customer Information */}
               <div>
                 <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Customer Information</h3>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                     <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Name</p>
                     <p className="text-gray-900 dark:text-white">{selectedPayment.user_name || 'N/A'}</p>
                   </div>
                   <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                     <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Email</p>
                     <p className="text-gray-900 dark:text-white">{selectedPayment.user_email || 'N/A'}</p>
                   </div>
                   {selectedPayment.user_phone && (
                     <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                       <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Phone</p>
                       <p className="text-gray-900 dark:text-white">{selectedPayment.user_phone}</p>
                     </div>
                   )}
                   {selectedPayment.user_whatsapp && (
                     <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                       <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">WhatsApp</p>
                       <p className="text-gray-900 dark:text-white">{selectedPayment.user_whatsapp}</p>
                     </div>
                   )}
                   {selectedPayment.user_address && (
                     <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg md:col-span-2">
                       <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Address</p>
                       <p className="text-gray-900 dark:text-white">{selectedPayment.user_address}</p>
                     </div>
                   )}
                   <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                     <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Order Date</p>
                     <p className="text-gray-900 dark:text-white">{formatDateSafe(selectedPayment.order_created_at || selectedPayment.created_at)}</p>
                   </div>
                   <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                     <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Payment Method</p>
                     <p className="text-gray-900 dark:text-white">{mapPaymentMethodToDisplay(selectedPayment.payment_method) || 'N/A'}</p>
                   </div>
                 </div>
               </div>

               {/* Order Items */}
               {selectedPayment.items && selectedPayment.items.length > 0 && (
                 <div>
                   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Order Items</h3>
                   <div className="space-y-4">
                     {selectedPayment.items.map((item, index) => (
                       <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                         {/* Product Header */}
                         <div className="flex items-center space-x-4 mb-4">
                           {/* Product Image */}
                           <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                             {item.product_image ? (
                               <img 
                                 src={item.product_image.startsWith('http') ? item.product_image : `/uploads/${item.product_image}`}
                                 alt={item.product_name}
                                 className="w-full h-full object-cover"
                                 onError={(e) => {
                                   e.target.style.display = 'none';
                                   e.target.nextSibling.style.display = 'flex';
                                 }}
                               />
                             ) : null}
                             <div className={`w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center ${item.product_image ? 'hidden' : ''}`}>
                               <Package className="w-8 h-8 text-gray-400" />
                             </div>
                           </div>
                           
                           {/* Product Info */}
                           <div className="flex-1">
                             <p className="text-lg font-medium text-gray-900 dark:text-white">{item.product_name}</p>
                             <p className="text-sm text-gray-500 dark:text-gray-400">Quantity: {item.quantity}</p>
                           </div>
                         </div>

                         {/* Pricing Details */}
                         <div className="space-y-3">
                           {/* License Information */}
                           {item.selected_license && (
                             <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                               <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                 {item.selected_license === 'extended' ? 'Extended License' : 'Regular License'}
                               </span>
                               <div className="text-right">
                                 <span className="font-medium">
                                   ₹{item.selected_license === 'extended' ? 
                                     (item.license_details?.extended_license_price || item.extended_license_price || 0) :
                                     (item.license_details?.regular_license_price || item.regular_license_price || 0)
                                   }
                                 </span>
                               </div>
                             </div>
                           )}

                           {/* Extended Support Information */}
                           {item.extended_support && (
                             <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                               <span className="text-sm font-medium text-green-600 dark:text-green-400">Extended Support</span>
                               <div className="text-right">
                                 <span className="font-medium">
                                   ₹{item.support_details?.extended_support_price || item.extended_support_price || 0}
                                 </span>
                               </div>
                             </div>
                           )}

                           {/* Addons Information */}
                           {item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (
                             <div className="py-2 border-b border-gray-200 dark:border-gray-600">
                               <div className="flex justify-between items-center mb-2">
                                 <span className="text-sm font-medium text-purple-600 dark:text-purple-400">Addons</span>
                               </div>
                               <div className="space-y-1">
                                 {item.selected_addons.map((addonId, addonIndex) => {
                                   const addon = (item.addon_details || item.addons)?.find(a => a.id === addonId);
                                   return addon ? (
                                     <div key={addonIndex} className="flex justify-between items-center pl-4">
                                       <span className="text-sm text-gray-600 dark:text-gray-400">{addon.name}</span>
                                       <div className="text-right">
                                         <span className="text-sm font-medium">₹{addon.price || 0}</span>
                                       </div>
                                     </div>
                                   ) : null;
                                 })}
                               </div>
                             </div>
                           )}

                           {/* Item Total */}
                           <div className="flex justify-between items-center pt-2">
                             <span className="text-lg font-semibold text-gray-900 dark:text-white">Total</span>
                             <span className="text-lg font-semibold text-gray-900 dark:text-white">
                               ₹{(() => {
                                 // Calculate total including support and addons
                                 let itemTotal = (item.price || 0) * (item.quantity || 1);
                               
                                 // Add support price if extended support is selected
                                 if (item.extended_support && (item.support_details?.extended_support_price || item.extended_support_price)) {
                                   itemTotal += (item.support_details?.extended_support_price || item.extended_support_price || 0) * (item.quantity || 1);
                                 }
                               
                                 // Add addon prices if addons are selected
                                 if (item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (item.addon_details || item.addons)) {
                                   item.selected_addons.forEach(addonId => {
                                     const addon = (item.addon_details || item.addons)?.find(a => a.id === addonId);
                                     if (addon && addon.price) {
                                       itemTotal += addon.price * (item.quantity || 1);
                                     }
                                   });
                                 }
                               
                                 return itemTotal;
                               })()}
                             </span>
                           </div>
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>
               )}

               {/* Billing Address */}
               {selectedPayment.billing_address && (
                 <div>
                   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Billing Address</h3>
                   <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                     <p className="text-gray-600 dark:text-gray-400">
                       {typeof selectedPayment.billing_address === 'string' 
                         ? selectedPayment.billing_address 
                         : JSON.stringify(selectedPayment.billing_address, null, 2)
                       }
                     </p>
                   </div>
                 </div>
               )}

               {/* Notes */}
               {(selectedPayment.notes || selectedPayment.order_notes) && (
                 <div>
                   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notes</h3>
                   <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                     <p className="text-gray-600 dark:text-gray-400">
                       {selectedPayment.notes || selectedPayment.order_notes}
                     </p>
                   </div>
                 </div>
               )}

               {/* Rating and Review (if available) */}
               {(selectedPayment.rating || selectedPayment.review) && (
                 <div>
                   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Customer Rating & Review</h3>
                   <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                     {selectedPayment.rating && (
                       <div className="mb-2 text-yellow-700 dark:text-yellow-300">
                         Rating: <span className="font-semibold">{selectedPayment.rating}/5</span>
                       </div>
                     )}
                     {selectedPayment.review && (
                       <div className="mb-2">
                         <p className="text-gray-700 dark:text-gray-300 italic">"{selectedPayment.review}"</p>
                       </div>
                     )}
                     {selectedPayment.review_date && (
                       <p className="text-xs text-yellow-700 dark:text-yellow-300">
                         Reviewed on: {new Date(selectedPayment.review_date).toLocaleString()}
                       </p>
                     )}
                   </div>
                 </div>
               )}

               {/* Payment Proof (if available) */}
               {selectedPayment.payment_proof && (
                 <div>
                   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Payment Proof</h3>
                   <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                     <div className="flex items-center justify-between">
                       <div className="flex-1">
                         <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                           {selectedPayment.payment_proof.originalName || selectedPayment.payment_proof.filename}
                         </p>
                         <p className="text-xs text-blue-700 dark:text-blue-300">
                           {selectedPayment.payment_proof.size ? `${(selectedPayment.payment_proof.size / 1024).toFixed(1)} KB` : ''} • {selectedPayment.payment_proof.type}
                         </p>
                         {selectedPayment.payment_proof.uploaded_at && (
                           <p className="text-xs text-blue-600 dark:text-blue-400">
                             Uploaded: {new Date(selectedPayment.payment_proof.uploaded_at).toLocaleString()}
                           </p>
                         )}
                       </div>
                       <div className="flex items-center space-x-2">
                         {selectedPayment.payment_proof.type?.startsWith('image/') && (
                           <button
                             onClick={() => {
                               const imageUrl = `/api/documents/${selectedPayment.payment_proof.filename}`;
                               window.open(imageUrl, '_blank');
                             }}
                             className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-100 dark:bg-blue-900/30 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                             title="View Image"
                           >
                             <FileText className="h-4 w-4" />
                           </button>
                         )}
                         {selectedPayment.payment_proof.type === 'application/pdf' && (
                           <button
                             onClick={() => {
                               const pdfUrl = `/api/documents/${selectedPayment.payment_proof.filename}`;
                               window.open(pdfUrl, '_blank');
                             }}
                             className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 bg-red-100 dark:bg-red-900/30 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                             title="View PDF"
                           >
                             <FileText className="h-4 w-4" />
                           </button>
                         )}
                         <button
                           onClick={() => {
                             const downloadUrl = `/api/documents/${selectedPayment.payment_proof.filename}/download`;
                             const link = document.createElement('a');
                             link.href = downloadUrl;
                             link.download = selectedPayment.payment_proof.originalName || selectedPayment.payment_proof.filename;
                             link.target = '_blank';
                             document.body.appendChild(link);
                             link.click();
                             document.body.removeChild(link);
                           }}
                           className="p-2 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 bg-green-100 dark:bg-green-900/30 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                           title="Download File"
                         >
                           <Download className="h-4 w-4" />
                         </button>
                       </div>
                     </div>
                     {selectedPayment.payment_proof.type?.startsWith('image/') && (
                       <div className="mt-4">
                         <img
                           src={`/api/documents/${selectedPayment.payment_proof.filename}`}
                           alt="Payment Proof"
                           className="max-w-full h-auto max-h-64 rounded-lg border border-gray-200 dark:border-gray-600"
                           onError={(e) => { e.target.style.display = 'none'; }}
                         />
                       </div>
                     )}
                     {selectedPayment.payment_proof.type === 'application/pdf' && (
                       <div className="mt-4">
                         <iframe
                           src={`/api/documents/${selectedPayment.payment_proof.filename}#toolbar=0`}
                           className="w-full h-64 rounded-lg border border-gray-200 dark:border-gray-600"
                           title="PDF Preview"
                           onError={(e) => { e.target.style.display = 'none'; }}
                         />
                       </div>
                     )}
                   </div>
                 </div>
               )}
             </div>
           </div>
         </div>,
         document.body
       )}
     </div>
   );
 };

export default EMIPaymentsManagement;
